import { Box, useScaleFactor, AHSeparator } from "arkh";
import {
  ActivityIndicator,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  PixelRatio,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import React, {
  ForwardedRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useWidgetSignalSubscription } from "./useWidgetSignalSubscription";

import { graphql, usePaginationFragment } from "react-relay";
import { SignalOverviewListItemMedium } from "./SignalOverviewListItemMedium";
import { WidgetSignalOverviewContentMediumPaginated$key } from "./__generated__/WidgetSignalOverviewContentMediumPaginated.graphql";
import { useDebouncedCallback } from "use-debounce";

const { containerStyle } = StyleSheet.create({
  containerStyle: { paddingBottom: 10 },
});

export type WidgetSignalOverviewListRef = {
  scrollToItem: (itemId: string | null) => void;
};

const WidgetSignalOverviewContentMediumC = (
  props: {
    widget: WidgetSignalOverviewContentMediumPaginated$key;
    isFocused?: boolean;
    interactive?: boolean;
    shouldDimInactive: boolean;
    onPressItem: (id: string) => void;
    setScroll: (itemId: string) => void;
    highlightedItemId?: string;
  },
  ref: ForwardedRef<WidgetSignalOverviewListRef>
) => {
  const {
    data: widget,
    hasNext,
    isLoadingNext,
    loadNext,
  } = usePaginationFragment(
    graphql`
      fragment WidgetSignalOverviewContentMediumPaginated on WidgetSignalOverview
      @refetchable(
        queryName: "WidgetSignalOverviewContentMediumPaginatedQuery"
      )
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 20 }
        after: { type: "String", defaultValue: null }
      ) {
        teams {
          id
        }
        signalContent: content {
          ... on WidgetSignalOverviewContentMedium {
            signals(first: $first, after: $after)
              @connection(key: "WidgetSignalOverviewContentMedium__signals") {
              __id
              edges {
                node {
                  id
                  ...SignalOverviewListItemMedium_Signal
                }
              }
            }
          }
        }
      }
    `,
    props.widget
  );

  const signals = useMemo(() => {
    return (widget?.signalContent?.signals?.edges ?? [])
      .map((edge) => edge.node)
      .filter((node) => node?.id);
  }, [widget]);

  const shared = useMemo(
    () => ({
      opacity: props.shouldDimInactive ? 0.2 : 1,
    }),
    [props.shouldDimInactive]
  );

  const renderRow = useCallback(
    ({ item }: { item: (typeof signals)[number]; index: number }) => (
      <View style={shared}>
        <SignalOverviewListItemMedium
          isFocused={item.id === props.highlightedItemId}
          signal={item}
          onPress={props.onPressItem}
        />
      </View>
    ),
    [shared, props.highlightedItemId, props.onPressItem]
  );

  const keyExtractor = useCallback((item: (typeof signals)[0]) => item.id, []);

  const signalIds = useMemo(() => signals.map(({ id }) => id), [signals]);
  const teamIds = useMemo(
    () => widget?.teams?.map(({ id }) => id) ?? [],
    [widget.teams]
  );

  useWidgetSignalSubscription(
    teamIds,
    signalIds,
    widget.signalContent?.signals?.__id ?? undefined,
    props.interactive
  );

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext && props.interactive !== false) {
      loadNext(20);
    }
  }, [hasNext, isLoadingNext, props.interactive, loadNext]);

  const sf = useScaleFactor();
  const ITEM_HEIGHT = useMemo(
    () => PixelRatio.roundToNearestPixel(40 * sf),
    [sf]
  );

  const getItemLayout = useCallback(
    (_data: any, index: number) => ({
      length: ITEM_HEIGHT,
      offset: ITEM_HEIGHT * index,
      index,
    }),
    [ITEM_HEIGHT]
  );

  const flatListRef = useRef<FlatList>(null);
  useImperativeHandle(ref, () => ({
    scrollToItem: (itemId) => {
      if (itemId) {
        const index = signals.findIndex((signal) => signal.id === itemId);
        if (index !== -1) {
          flatListRef.current?.scrollToIndex({ index, animated: true });
        }
      } else {
        flatListRef.current?.scrollToOffset({
          offset: 0,
          animated: true,
        });
      }
    },
  }));

  const setScroll = props.setScroll;
  const lastScrollId = useRef<string | null>(null);

  const _onScroll = useDebouncedCallback(
    useCallback(
      (y: number) => {
        const index = Math.max(0, Math.floor(y / ITEM_HEIGHT - 1));
        const item = signals[index];
        if (item && lastScrollId.current !== item.id) {
          setScroll(item.id);
          lastScrollId.current = item.id;
        }
      },
      [ITEM_HEIGHT, setScroll, signals]
    ),
    200,
    {
      leading: true,
      maxWait: 2000,
    }
  );

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (props.isFocused) {
        _onScroll(
          event.nativeEvent.targetContentOffset
            ? event.nativeEvent.targetContentOffset.y
            : event.nativeEvent.contentOffset.y
        );
      }
    },
    [_onScroll, props.isFocused]
  );

  const ListFooterComponent = useMemo(
    () => (
      <Box style={{ flex: 1 }} spaceTop="xxlarge">
        {isLoadingNext ? <ActivityIndicator /> : null}
      </Box>
    ),
    [isLoadingNext]
  );

  const ItemSeparatorComponent = useCallback(() => <AHSeparator />, []);

  return (
    <FlatList
      ref={flatListRef}
      initialNumToRender={15}
      showsVerticalScrollIndicator={Platform.OS !== "web"}
      ListFooterComponent={ListFooterComponent}
      ItemSeparatorComponent={ItemSeparatorComponent}
      keyExtractor={keyExtractor}
      data={signals}
      onEndReachedThreshold={0.5}
      onScroll={onScroll}
      renderItem={renderRow}
      scrollEnabled={!(props.interactive === false)}
      onEndReached={onEndReached}
      getItemLayout={getItemLayout}
      contentContainerStyle={containerStyle}
    />
  );
};

export const WidgetSignalOverviewContentMedium = React.memo(
  React.forwardRef(WidgetSignalOverviewContentMediumC)
);
